import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Input, Select, Table, Tabs, Tag, Upload, message} from 'antd'
import {
    commitImport,
    exportStandardGroups,
    downloadImportTemplate,
    getStages,
    importIssues,
    previewImport,
} from '../../../../services/api/apiProductionPlanning'
import {GROUP_NAME_MAX_LENGTH, GROUP_NOTE_MAX_LENGTH, GROUP_VERIFY_LABELS, MIN_CHANGE_REASON} from '../../constants'
import {DASH, ceilTo, formatDecimal, formatExactRate, formatInt, formatRate} from '../../helpers/format'
import {canImportStandardConfig} from '../../helpers/permission'
import {parseImportFile} from '../../helpers/importFile'
import {priorityOf} from '../../helpers/priority'
import {downloadBlob} from '../../helpers/download'
import {errorMessage} from '../../helpers/apiError'
import Notice from '../shared/Notice'

const {Option} = Select
const {TabPane} = Tabs

/**
 * Bốn công đoạn chưa đo được định mức từ log thật (API-CHANGES-FOR-FE.md §5).
 *
 * Log của chúng do tài khoản tích hợp `3D Partner` ghi nên không quy được về giờ công. File mẫu vẫn
 * sinh dòng cho bốn công đoạn này, nhưng CON SỐ TRONG ĐÓ LÀ BỊA — chỉ để minh hoạ định dạng. Nạp
 * thẳng lên là đưa số bịa vào hệ thống, nên phải nói rõ ngay tại bước tải file mẫu.
 */
const UNMEASURED_STAGES = ['in', 'cat', 'khac', 'hoanthien']

/**
 * Các cột dữ liệu (SRS §15, tóm tắt ở UISPEC §2).
 *
 * Hai cột tên vì hai nguồn đang đặt tên khác nhau và cả hai đều đang được dùng:
 *   - `name`  — theo file mẫu service đang trả (`GET /kpi/standard-groups/template`), camelCase.
 *   - `alias` — theo đặc tả M16, snake_case. File .xlsx do người dùng tự dựng theo SRS sẽ mang tên này.
 * Nhập được cả .xlsx lẫn .csv nên trình nhập phải chấp nhận cả hai cách đặt tiêu đề.
 *
 * Cột G: file mẫu đang trả `id` (rỗng = tạo mới, có giá trị = cập nhật nhóm cũ), còn SRS mô tả
 * `change_reason`. Hai thứ khác nhau chứ không phải một cột đổi tên — cần chốt với BA, xem ghi chú
 * ở đầu component.
 */
/**
 * Mô tả cột của file định mức.
 *
 * ⚠️ KHÔNG in chữ cái cột (A/B/C…): service đọc file theo **TÊN cột**, không theo vị trí — file đảo
 *    thứ tự vẫn nạp đúng, cột lạ bị bỏ qua. In chữ cái cột là dạy một ràng buộc KHÔNG có thật, rồi
 *    người dùng sẽ đi sửa vị trí cột khi gặp lỗi thay vì sửa tên cột.
 */
const COLUMNS_SPEC = [
    {name: 'stageCode', type: 'Text', required: '✅'},
    {name: 'skuScope', type: 'Text', required: '✅ — all / list'},
    {name: 'skuList', type: 'Text', required: 'Khi skuScope = list. Ngăn cách bằng dấu phẩy — nhận cả ; và |'},
    {
        name: 'materialCode',
        type: 'Text',
        required: '❌ — để trống = mọi NVL. NHIỀU mã ngăn bằng dấu phẩy. Phải định dạng cột là Text',
    },
    {
        name: 'rate',
        type: 'Number',
        required: '✅ — số từ 0 trở lên. Điền 0 để khai “SKU/NVL này không đi qua công đoạn” (0 giờ thật)',
    },
    {name: 'effectiveFrom', type: 'Date', required: '✅ — cho phép ngày trong quá khứ'},
    {
        name: 'effectiveTo',
        type: 'Date',
        required: '❌ — để trống = còn hiệu lực. Mốc này KHÔNG tính vào khoảng (đến hết ngày hôm trước)',
    },
    {
        name: 'priority',
        type: 'Number',
        required: '❌ — để trống là chấm tự động. Nhóm all + không NVL phải để trống hoặc 0',
    },
    {name: 'verifyStatus', type: 'Text', required: '❌ — unverified / verified. Để trống = unverified'},
    {name: 'note', type: 'Text', required: `❌ — tối đa ${GROUP_NOTE_MAX_LENGTH} ký tự`},
    {
        name: 'name',
        type: 'Text',
        required:
            `❌ — tên gợi nhớ, KHÔNG cần duy nhất, tối đa ${GROUP_NAME_MAX_LENGTH} ký tự. ` +
            'Nhóm all + không NVL là nhóm mặc định nên phải để trống',
    },
]

/**
 * SCR-M16-005 — Nhập / xuất Excel.
 *
 * Đây là đường khai chính khi thiết lập lần đầu: mô hình một nhóm một NVL rất hợp với Excel, mỗi
 * dòng Excel là một nhóm.
 *
 * ⚠️ Màn hình này chưa có mockup ở bản v9.0 — bố cục dưới đây dựng theo đặc tả §2 → §5. Cần review
 *    với PO/BA trước khi coi là chốt.
 *
 * ⚠️ Bất đối xứng cố ý của luồng nhập (BR-M16-47): thiếu cột bắt buộc thì từ chối **cả file**; từ
 *    bước 3 trở đi thì từ chối **theo dòng**. Đừng gộp hai cách xử lý lại.
 *
 * Nhập nhận CẢ .xlsx VÀ .csv. Kéo theo hai việc trình nhập phía service phải làm, vì giao diện chỉ
 * đẩy nguyên file lên chứ không đọc:
 *   1. Nhận cả hai cách đặt tiêu đề cột — camelCase như file mẫu đang trả, và snake_case theo SRS.
 *   2. Với .csv thì không có khái niệm sheet, đọc thẳng bảng duy nhất.
 *
 * ⚠️ CHƯA CHỐT: cột G. File mẫu trả `id` (khoá để cập nhật nhóm cũ), SRS mô tả `change_reason`
 *    (lý do sửa khi lệch quá ±30%). Đây là hai trường khác nhau chứ không phải một cột đổi tên —
 *    nếu cần cả hai thì file phải có tám cột. Chờ BA quyết trước khi backend làm importer.
 */
/**
 * Màn này CHỈ chạy trong modal của Chỉ số tiêu chuẩn (`StageListPage`) — không còn mục menu, không
 * còn route riêng. Nhánh "chạy như một trang" đã thành code chết sau khi bỏ route nên gỡ hẳn.
 *
 * Tab đang mở do MÀN CHA nắm và truyền xuống qua `mode`, KHÔNG đọc lại `location.search`. Lý do:
 * cha là NƠI DUY NHẤT đọc/ghi `lookup` và `mode` trên URL; đọc lại ở đây là dựng nguồn sự thật thứ
 * hai cho cùng một giá trị, và hai nguồn đó sẽ lệch nhau đúng vào lúc khó tìm nhất — giữa lúc cha
 * vừa điều hướng mà lượt render này chưa nhận `location` mới.
 *
 * ⚠️ Cũng chính vì thế `activeTab` bên dưới phải tự gác `canImport` lần nữa: nếu `mode='import'` lọt
 *    xuống trong khi tab Nhập không được render, `rc-tabs` không có pane nào khớp `activeKey` và
 *    hiện modal RỖNG — không lỗi, không cảnh báo, không đường thoát.
 *
 * `onModeChange` ghi NGƯỢC lên URL khi người dùng bấm đổi tab, nên link copy từ thanh địa chỉ luôn
 * mở đúng tab người gửi đang xem (trước đây `defaultActiveKey` là một chiều: đổi tab không cập nhật
 * URL, người nhận mở ra tab khác).
 */
function ImportExportPage({onClose, mode, onModeChange}) {
    const canImport = canImportStandardConfig()

    // Không có quyền nhập thì KHÔNG render tab Nhập, và ép tab đang mở về Xuất. Màn cha đã hạ `mode`
    // rồi, nhưng component này cũng tự gác để không phụ thuộc vào một chỗ duy nhất.
    const activeTab = mode === 'import' && canImport ? 'import' : 'export'

    const [stages, setStages] = useState([])
    const [exportScope, setExportScope] = useState('all')
    const [exporting, setExporting] = useState(false)
    const [downloadingTemplate, setDownloadingTemplate] = useState(false)
    // Giữ luôn File: không có token xem trước, muốn ghi thì phải gửi lại đúng file đó.
    const [pending, setPending] = useState(null)
    const [preview, setPreview] = useState(null)
    const [issues, setIssues] = useState([])
    // Nội dung file đọc ngay ở trình duyệt, chỉ để bung chi tiết từng công đoạn.
    const [fileRows, setFileRows] = useState([])
    const [uploading, setUploading] = useState(false)
    const [committing, setCommitting] = useState(false)
    /*
     * Lý do thay đổi (VAL-05). Service chặn lượt ghi khi có nhóm lệch quá 30% so với bản đang chạy
     * mà không kèm lý do >= 10 ký tự — và nó đã trả sẵn `preview.blockers` ở bước xem trước ĐÚNG để
     * màn này thu lý do. Trước đây màn không đọc `blockers` và `commitImport` gọi không tham số thứ
     * hai, nên VAL-05 là ngõ cụt: bấm ghi ra bảng lỗi một dòng trống, không đường nào thoả được nó.
     */
    const [changeReason, setChangeReason] = useState('')

    useEffect(() => {
        getStages().then(setStages).catch(() => setStages([]))
    }, [])

    /**
     * Tải file mẫu. Phải đi qua `request` + blob: service đã siết auth nên thẻ <a> (không gửi được
     * header Authorization) sẽ nhận 401.
     */
    const handleDownloadTemplate = async () => {
        setDownloadingTemplate(true)

        try {
            downloadBlob(await downloadImportTemplate(), 'standard-groups-template.csv')
            message.success('Đã tải file mẫu')
        } catch (e) {
            message.error(errorMessage(e) || 'Không tải được file mẫu. Vui lòng thử lại')
        } finally {
            setDownloadingTemplate(false)
        }
    }

    const handleExport = async () => {
        setExporting(true)

        try {
            // Service trả CSV dạng stream, phải tự dựng link tải — chỉ `await` rồi báo thành
            // công thì không có file nào rơi xuống máy (xem helpers/download.js).
            const blob = await exportStandardGroups(exportScope === 'all' ? undefined : exportScope)
            const suffix = exportScope === 'all' ? '' : `-${exportScope}`

            downloadBlob(blob, `standard-groups${suffix}.csv`)

            message.success('Đã tải file định mức')
        } catch (e) {
            message.error(errorMessage(e) || 'Không xuất được file. Vui lòng thử lại')
        } finally {
            setExporting(false)
        }
    }

    const runPreview = async (file) => {
        setUploading(true)
        setPreview(null)
        setIssues([])
        setPending(null)
        setFileRows([])
        setChangeReason('')

        try {
            const result = await previewImport(file)

            setPreview(result)
            setPending(file)

            // Đọc file sau khi service đã nhận: hỏng bước này thì bảng tổng vẫn dùng được, chỉ mất
            // phần bung chi tiết. Không để lỗi đọc file làm hỏng cả luồng nhập.
            try {
                setFileRows(await parseImportFile(file))
            } catch (parseError) {
                setFileRows([])
            }
        } catch (e) {
            // File sai dòng nào là hỏng cả lượt: không có bản xem trước, chỉ có danh sách lỗi kèm số
            // dòng. Lỗi đã được interceptor trải phẳng nên đọc thẳng `e.data`, không phải
            // `e.response.data`.
            setIssues(importIssues(e))
            message.error(errorMessage(e) || 'Không đọc được file')
        } finally {
            setUploading(false)
        }
    }

    /**
     * `beforeUpload` PHẢI trả `false` đồng bộ.
     *
     * rc-upload coi mọi giá trị có `.then` là "xử lý xong rồi cứ tải lên" (AjaxUploader `uploadFiles`),
     * mà hàm `async` thì luôn trả Promise — kể cả khi thân hàm `return false`. Lúc đó nó tự POST vào
     * `props.action`, và antd để mặc định `action: ''`; URL rỗng trỏ về chính trang đang mở, nên
     * trình duyệt POST vào `/config/standard-time/import-export` và server trả
     * "Cannot POST /config/standard-time/import-export".
     *
     * Vậy nên tách đôi: hàm này trả `false` ngay, còn phần đọc file chạy nền không cần chờ.
     */
    const handleUpload = (file) => {
        runPreview(file)

        return false
    }

    const handleCommit = async () => {
        setCommitting(true)
        setIssues([])

        try {
            const result = await commitImport(pending, changeReason.trim() || undefined)
            const stageCount = (result.stages || []).length

            message.success(`Đã ghi ${formatInt(result.file.groupCount)} nhóm cho ${formatInt(stageCount)} công đoạn`)
            setPreview(null)
            setPending(null)
            setFileRows([])
            setChangeReason('')
        } catch (e) {
            // Ghi thất bại thì KHÔNG công đoạn nào được lưu.
            setIssues(importIssues(e))
            message.error(errorMessage(e) || 'Không ghi được dữ liệu. Không dòng nào được lưu')
        } finally {
            setCommitting(false)
        }
    }

    const stageName = (stageCode) => {
        const stage = stages.find((item) => item.stageCode === stageCode)

        return stage ? stage.name : stageCode
    }

    /**
     * Xem trước theo CÔNG ĐOẠN chứ không theo dòng — vì lúc ghi, service thay toàn bộ nhóm của mỗi
     * công đoạn có trong file. `expiringCount` là số nhóm đang chạy sẽ hết hiệu lực; đây mới là con
     * số người duyệt cần nhìn trước khi bấm ghi.
     */
    const previewColumns = [
        {
            title: 'Công đoạn',
            dataIndex: 'stageCode',
            render: (stageCode) => <strong>{stageName(stageCode)}</strong>,
        },
        {
            title: 'Nhóm trong file',
            dataIndex: 'incomingCount',
            width: 130,
            align: 'right',
            render: formatInt,
        },
        {
            title: 'Nhóm đang có',
            dataIndex: 'currentCount',
            width: 130,
            align: 'right',
            render: formatInt,
        },
        {
            title: 'Sẽ hết hiệu lực',
            dataIndex: 'expiringCount',
            width: 140,
            align: 'right',
            render: (expiringCount) => (expiringCount
                ? <span className="VcDanger">{formatInt(expiringCount)}</span>
                : formatInt(expiringCount)),
        },
    ]

    const totals = (preview ? preview.stages : []).reduce((acc, row) => ({
        incoming: acc.incoming + (row.incomingCount || 0),
        current: acc.current + (row.currentCount || 0),
        expiring: acc.expiring + (row.expiringCount || 0),
    }), {incoming: 0, current: 0, expiring: 0})

    /** Chi tiết từng dòng của một công đoạn, đọc thẳng từ file người dùng vừa chọn. */
    const detailColumns = [
        {title: 'Dòng', dataIndex: 'row', width: 64},
        {
            title: 'Tên nhóm',
            dataIndex: 'name',
            width: 160,
            ellipsis: true,
            render: (name) => (name ? name : DASH),
        },
        {
            title: 'Phạm vi SKU',
            key: 'scope',
            width: 260,
            render: (text, row) => (row.skuScope === 'all'
                ? <Tag>Mọi SKU</Tag>
                : (
                    <span>
                        <Tag color="blue">{formatInt(row.skuList.length)} SKU</Tag>
                        <span className="VcMuted">{row.skuList.join(', ')}</span>
                    </span>
                )),
        },
        {
            title: 'Nguyên vật liệu',
            dataIndex: 'materialCodes',
            width: 160,
            render: (codes) => ((codes || []).length
                ? (codes || []).map((code) => <code key={code} className="VcMaterialCode">{code}</code>)
                : <span className="VcMuted">mọi NVL</span>),
        },
        {
            title: 'Chỉ số',
            dataIndex: 'rate',
            width: 130,
            align: 'right',
            /*
             * In số SẼ ĐƯỢC GHI, và nói rõ khi nó khác số trong file.
             *
             * ⚠️ Service làm tròn LÊN thành số nguyên (`ceilRate`) ở mọi đường ghi, còn parser thì cố
             *    ý cho số lẻ đi qua. In nguyên văn ô trong file là để màn hình nói "Chưa ghi gì — xem
             *    bảng dưới rồi mới xác nhận" trong khi con số người dùng xác nhận (0,4) khác con số
             *    được ghi (1) tới 150%. Đường nhập tay đã bịt bằng `roundRate` lúc rời ô; đường Excel
             *    là lối duy nhất còn lại.
             */
            render: (rate) => {
                const willStore = ceilTo(rate)
                const changed = rate !== '' && Number(rate) !== Number(willStore)

                return (
                    <span>
                        <code className="VcRate">{formatRate(rate)}</code>
                        {changed && <span className="VcMuted"> (file: {rate})</span>}
                    </span>
                )
            },
        },
        {title: 'Hiệu lực từ', dataIndex: 'effectiveFrom', width: 120},
        {
            title: 'Đến ngày',
            dataIndex: 'effectiveTo',
            width: 120,
            render: (to) => (to || <span className="VcMuted">còn hiệu lực</span>),
        },
        {
            title: 'Độ ưu tiên',
            dataIndex: 'priority',
            width: 140,
            align: 'right',
            // Ô trống = để hệ thống chấm theo điều kiện. Nhưng chỉ hiện chữ "tự động" là giấu đúng
            // thứ bản xem trước sinh ra để cho xem: ba dòng cùng ghi "tự động" có thể ra 0, 10 và 50
            // — tức thứ tự thắng/thua hoàn toàn khác nhau. Tính sẵn và in ra con số.
            render: (priority, row) => (priority === ''
                ? (
                    <span className="VcMuted">
                        tự động <code>{formatInt(priorityOf(row))}</code>
                    </span>
                )
                : <code>{priority}</code>),
        },
        {
            title: 'Trạng thái',
            dataIndex: 'verifyStatus',
            width: 120,
            // Ô rỗng ra `unverified` — đúng mặc định của service, hiện thẳng nhãn đó để người dùng
            // thấy trước khi ghi chứ không phải đoán.
            //
            // ⚠️ `.toLowerCase()` để khớp service: nó `.trim().toLowerCase()` trước khi so, nên file
            //    ghi `Verified` được NHẬN là verified. So phân biệt hoa/thường ở đây là bản xem trước
            //    nói ngược với thứ sắp ghi, ở đúng cột thêm vào để người dùng khỏi phải đoán.
            render: (verifyStatus) => ((verifyStatus || '').trim().toLowerCase() === 'verified'
                ? <Tag color="green">{GROUP_VERIFY_LABELS.verified}</Tag>
                : <Tag>{GROUP_VERIFY_LABELS.unverified}</Tag>),
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            ellipsis: true,
            render: (note) => (note ? <span className="VcMuted">{note}</span> : DASH),
        },
    ]

    /*
     * Nhóm lệch quá 30% so với bản đang chạy, gom từ `preview.blockers` mà service trả về ở bước xem
     * trước. Bước xem trước CỐ Ý không ném VAL-05 (route preview không nhận `@Body()` nên không
     * truyền được lý do) — nó trả blockers ra để màn này hỏi lý do TRƯỚC khi ghi, thay vì để người
     * dùng bấm ghi rồi ăn một bảng lỗi.
     */
    const rateBlockers = ((preview && preview.blockers) || []).flatMap((blocker) =>
        (blocker.rateViolations || []).map((violation) => ({...violation, stageCode: blocker.stageCode}))
    )
    const needsReason = !!rateBlockers.length
    const reasonTooShort = needsReason && changeReason.trim().length < MIN_CHANGE_REASON

    const issueColumns = [
        {title: 'Dòng', dataIndex: 'row', width: 70},
        {
            title: 'Cột',
            dataIndex: 'column',
            width: 140,
            render: (column) => (column ? <code>{column}</code> : DASH),
        },
        {title: 'Mã', dataIndex: 'code', width: 90},
        {title: 'Lý do', dataIndex: 'message', render: (text) => <span className="VcDanger">{text}</span>},
    ]

    return (
        <div className="VcEmbeddedPage VcImportExportPage">
            <div className="VcPageHead">
                <h1 className="VcPageTitle">Nhập / xuất Excel</h1>
                <div className="VcPageActions">
                    <Button onClick={onClose}>Đóng</Button>
                </div>
            </div>

            {/*
              * Đặt NGOÀI `<Tabs>`: tab "Nhập dữ liệu" chỉ render khi `canImport`, nên mọi nhánh
              * `!canImport` nằm BÊN TRONG nó là code chết. Người thiếu quyền mở modal (qua link cũ
              * `?mode=import`, hoặc bấm "Xuất dữ liệu") sẽ thấy một modal tên "Nhập / xuất Excel"
              * mà chỉ có đúng một tab — không có dòng này thì họ không có cách nào biết vì sao.
              */}
            {!canImport && (
                <Notice level="warning">
                    Bạn không có quyền nhập dữ liệu nên tab Nhập được ẩn. Vẫn xem và xuất được file.
                </Notice>
            )}

            <Tabs activeKey={activeTab} onChange={onModeChange}>
                {canImport && <TabPane tab="Nhập dữ liệu" key="import">
                    {/*
                      * Cảnh báo định dạng hiển thị CỐ ĐỊNH trên màn nhập (§2.1). Đây là lý do phải
                      * luôn bắt đầu từ file mẫu: mã NVL 0205 bị Excel đổi sang định dạng Số sẽ thành
                      * 205 và cả dòng bị từ chối (BR-M16-49).
                      */}
                    {/*
                      * Mức `warning`, KHÔNG phải `error`. Đây là cảnh báo đứng thường trực (§2.1);
                      * tô đỏ liên tục thì đúng vào cái bẫy mà Notice.js đã ghi — người dùng quen mắt
                      * rồi bỏ qua mọi cảnh báo. Màu đỏ để dành cho "File có N lỗi, chưa ghi gì cả".
                      */}
                    <Notice level="warning">
                        Cột mã NVL phải để định dạng <strong>Text</strong>. Excel tự đổi sang Số thì
                        0205 thành 205 và cả dòng bị từ chối. File mẫu đã đặt sẵn đúng định dạng —
                        nhưng <strong>mở .csv bằng Excel rồi lưu lại là hỏng</strong>: sửa .csv bằng
                        trình soạn thảo văn bản, hoặc dùng Excel thì lưu thành .xlsx.
                    </Notice>

                    <div className="VcImportSteps">
                        <div className="VcStep">
                            <div className="VcStepTitle">1. Tải file mẫu</div>
                            <Button loading={downloadingTemplate} onClick={handleDownloadTemplate}>
                                Tải file mẫu
                            </Button>
                            <div className="VcHint">
                                File mẫu dựng từ dữ liệu đang có. Nạp lại y nguyên thì giá trị không đổi,
                                nhưng <strong>không phải là cập nhật tại chỗ</strong>: mỗi công đoạn có mặt
                                trong file sẽ được thay TOÀN BỘ nhóm — nhóm đang có chuyển sang hết hiệu
                                lực, dòng trong file thành nhóm mới. Xem lại được ở màn Lịch sử.
                            </div>
                            {/*
                              * Đặt ngay tại bước tải file mẫu vì nó nói về NỘI DUNG của chính file
                              * đó. Để trên đầu màn thì nó chỉ là thêm một banner nữa để lướt qua,
                              * mà bỏ đi thì người dùng nạp thẳng số ví dụ thành số chính thức.
                              */}
                            <div className="VcHint VcWarning">
                                Bốn công đoạn{' '}
                                {UNMEASURED_STAGES.map((stageCode, index) => (
                                    <React.Fragment key={stageCode}>
                                        {index > 0 && ', '}
                                        <code>{stageCode}</code>
                                    </React.Fragment>
                                ))}
                                {' '}chưa đo được định mức từ log thật, nên dòng của chúng trong file
                                mẫu chỉ là <strong>số ví dụ</strong> — phải thay bằng số đo thật trước
                                khi nạp.
                            </div>
                        </div>

                        <div className="VcStep">
                            <div className="VcStepTitle">2. Tải file lên</div>
                            {/*
                              * Nhận cả .xlsx và .csv: file mẫu service trả về là .csv, còn người dùng
                              * quen dựng bảng bằng Excel. Chặn đúng .xlsx thì loại luôn chính file mẫu
                              * mà nút bên cạnh vừa tải xuống.
                              */}
                            <Upload
                                accept=".xlsx,.csv"
                                showUploadList={false}
                                disabled={uploading}
                                beforeUpload={handleUpload}
                            >
                                <Button loading={uploading}>
                                    Chọn file .xlsx hoặc .csv
                                </Button>
                            </Upload>
                            <div className="VcHint">
                                File .csv phải mã hoá UTF-8, các cột ngăn nhau bằng dấu phẩy — file mẫu
                                đã đúng sẵn. Dòng đầu tiên là tiêu đề cột.
                            </div>
                            {/*
                              * Cảnh báo này chỉ dành cho người sửa .csv bằng trình soạn thảo văn bản.
                              * Sửa bằng Excel thì Excel tự bọc nháy, không bao giờ gặp.
                              */}
                            <div className="VcHint VcWarning">
                                Nhiều SKU trong cột <code>skuList</code> cũng ngăn bằng dấu phẩy, nên ô
                                đó <strong>phải bọc trong nháy kép</strong>:{' '}
                                <code>"SKU_A, SKU_B"</code>. Quên nháy thì dấu phẩy tách luôn cột và cả
                                dòng lệch sang phải. Sửa bằng Excel thì không phải lo — Excel tự bọc.
                            </div>
                        </div>
                    </div>

                    <div className="VcSpecTable">
                        <div className="VcStepTitle">Cấu trúc {COLUMNS_SPEC.length} cột dữ liệu</div>
                        <Table
                            className="VcTable"
                            rowKey="name"
                            size="small"
                            pagination={false}
                            dataSource={COLUMNS_SPEC}
                            columns={[
                                {title: 'Tên tiêu đề', dataIndex: 'name', render: (name) => <code>{name}</code>},
                                {title: 'Kiểu ô', dataIndex: 'type', width: 110},
                                {title: 'Bắt buộc', dataIndex: 'required'},
                            ]}
                        />
                        <div className="VcHint">
                            File .xlsx: dữ liệu nằm ở sheet <strong>DINH_MUC</strong>; sheet{' '}
                            <strong>DANH_MUC</strong> chỉ để tra cứu giá trị hợp lệ và bị bỏ qua khi
                            nhập. File .csv chỉ có một bảng, không có khái niệm sheet — chỉ cần đủ
                            các cột bắt buộc — service đọc theo TÊN cột nên thứ tự không quan trọng, và cột lạ
                            bị bỏ qua. Dòng ĐẦU TIÊN phải là dòng tiêu đề, không để dòng trống phía trên.
                        </div>
                    </div>

                    {!!issues.length && (
                        <div className="VcPreview">
                            {/*
                              * KHÔNG có bản xem trước khi file sai: service từ chối cả lượt, chưa ghi
                              * gì. Lỗi gắn số dòng để mở file ra sửa đúng chỗ.
                              */}
                            {/*
                              * Không phải lỗi nào cũng nằm TRONG file: VAL-05 (lệch chỉ số quá 30%)
                              * đòi một lý do thay đổi, sửa file bao nhiêu lần cũng không qua được.
                              * Câu hướng dẫn vì thế bám theo việc lỗi có gắn số dòng hay không —
                              * chỉ đường sai còn tệ hơn không chỉ đường.
                              */}
                            <Notice level="error">
                                File có {formatInt(issues.length)} lỗi — <strong>chưa ghi gì cả</strong>.{' '}
                                {issues.some((issue) => issue.row)
                                    ? 'Sửa đúng các dòng dưới đây rồi tải lên lại.'
                                    : 'Xem lý do bên dưới — có thể không cần sửa file.'}
                            </Notice>

                            <Table
                                className="VcTable"
                                rowKey={(row, index) => `${row.row}-${row.column || ''}-${index}`}
                                size="small"
                                pagination={false}
                                columns={issueColumns}
                                dataSource={issues}
                                rowClassName={() => 'VcRow--unconfigured'}
                            />
                        </div>
                    )}

                    {preview && (
                        <div className="VcPreview">
                            <Notice level={preview.stages.some((row) => row.expiringCount) ? 'warning' : 'success'}>
                                Đọc được <strong>{formatInt(preview.file.rowCount)}</strong> dòng →{' '}
                                <strong>{formatInt(preview.file.groupCount)}</strong> nhóm điều kiện, thuộc{' '}
                                <strong>{formatInt(preview.stages.length)}</strong> công đoạn.{' '}
                                <strong>Chưa ghi gì</strong> — xem bảng dưới rồi mới xác nhận.
                                <div className="VcNoticeSub">
                                    <strong>Lưu ý:</strong> nhập file là <strong>THAY TOÀN BỘ</strong> nhóm của các công đoạn có
                                    trong file: mọi nhóm đang có sẽ hết hiệu lực, mọi dòng trong file thành
                                    nhóm mới. Không có cách cập nhật riêng từng nhóm qua file — muốn sửa một
                                    nhóm thì xuất file ra, sửa, rồi nạp lại cả công đoạn. Bản cũ không mất,
                                    xem ở nút Lịch sử của công đoạn.
                                </div>
                            </Notice>

                            {/*
                              * Cảnh báo riêng của .xlsx: Excel ép ô mã NVL về số làm mất số 0 đứng đầu.
                              * Không phải lỗi nên vẫn ghi được, nhưng phải nói rõ trước khi ghi.
                              */}
                            {!!(preview.warnings || []).length && (
                                <Notice level="warning">
                                    {preview.warnings.map((warning, index) => (
                                        <div key={index}>Dòng {warning.row}: {warning.message}</div>
                                    ))}
                                </Notice>
                            )}

                            <Notice level="warning">
                                Ghi theo <strong>công đoạn</strong>, không theo dòng. Mỗi công đoạn có mặt
                                trong file sẽ bị thay <strong>toàn bộ</strong> nhóm điều kiện — nhóm đang
                                chạy mà không có trong file sẽ hết hiệu lực. Công đoạn không xuất hiện
                                trong file thì không bị đụng tới.
                            </Notice>

                            <Table
                                className="VcTable"
                                rowKey="stageCode"
                                size="small"
                                pagination={false}
                                columns={previewColumns}
                                dataSource={preview.stages}
                                rowClassName={(row) => (row.expiringCount ? 'VcRow--warning' : '')}
                                // Bung ra xem từng dòng của công đoạn đó. Chỉ bật khi đọc được file:
                                // không có dữ liệu mà vẫn hiện mũi tên là mời người dùng bấm vào chỗ trống.
                                expandable={fileRows.length ? {
                                    expandedRowRender: (row) => (
                                        <Table
                                            className="VcTable"
                                            rowKey="row"
                                            size="small"
                                            pagination={false}
                                            columns={detailColumns}
                                            dataSource={fileRows.filter((item) => item.stageCode === row.stageCode)}
                                        />
                                    ),
                                    rowExpandable: (row) => fileRows.some((item) => item.stageCode === row.stageCode),
                                } : undefined}
                                summary={() => (
                                    <Table.Summary.Row>
                                        {/* Bật `expandable` là antd chèn thêm một cột mũi tên ở
                                            đầu; không bù ô trống thì cả dòng tổng lệch một cột. */}
                                        {!!fileRows.length && <Table.Summary.Cell/>}
                                        <Table.Summary.Cell>
                                            <strong>Tổng {formatInt(preview.stages.length)} công đoạn</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <strong>{formatInt(totals.incoming)}</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <strong>{formatInt(totals.current)}</strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell align="right">
                                            <strong className={totals.expiring ? 'VcDanger' : ''}>
                                                {formatInt(totals.expiring)}
                                            </strong>
                                        </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                )}
                            />

                            {needsReason && (
                                <div className="VcChangeReason">
                                    <Notice level="warning">
                                        <strong>{formatInt(rateBlockers.length)}</strong> nhóm có chỉ số lệch quá{' '}
                                        <strong>30%</strong> so với bản đang chạy — bắt buộc nhập lý do thay đổi.
                                        <div className="VcNoticeSub">
                                            {rateBlockers.map((blocker, index) => (
                                                /*
                                                 * `formatExactRate`, KHÔNG phải `formatRate`.
                                                 *
                                                 * `formatRate` gọi `ceilTo(_, RATE_MAX_DECIMALS)` mà
                                                 * `RATE_MAX_DECIMALS = 0`, nên `0,4 → 0,6` in ra thành
                                                 * "1 → 1 (lệch 50%)": màn hình nói không có gì đổi mà vẫn
                                                 * bắt gõ lý do. `deltaPercent` do service tính trên số
                                                 * THÔ nên hai vế phải in thô mới khớp nhau. Cùng khuôn
                                                 * `from → to` mà HistoryDrawer đang dùng.
                                                 */
                                                <div key={`${blocker.stageCode}-${index}`}>
                                                    {stageName(blocker.stageCode)}: {formatExactRate(blocker.oldRate)} →{' '}
                                                    {formatExactRate(blocker.newRate)}{' '}
                                                    (lệch {formatDecimal(blocker.deltaPercent, 1)}%)
                                                </div>
                                            ))}
                                        </div>
                                    </Notice>

                                    {/*
                                      * Viền đỏ bằng CLASS, không bằng prop `status`: prop đó vào antd
                                      * từ 4.19, bản cài ở đây là 4.5.4 nên nó rơi thẳng xuống thẻ
                                      * <textarea> thành thuộc tính HTML rác và ô không bao giờ đổi màu.
                                      */}
                                    <Input.TextArea
                                        rows={2}
                                        value={changeReason}
                                        maxLength={200}
                                        className={reasonTooShort ? 'VcInputError' : ''}
                                        placeholder={`Vì sao đổi chỉ số? Tối thiểu ${MIN_CHANGE_REASON} ký tự — câu này vào nhật ký thay đổi.`}
                                        onChange={(event) => setChangeReason(event.target.value)}
                                    />
                                    {/*
                                      * Nói ra VÌ SAO nút Ghi đang khoá.
                                      *
                                      * ⚠️ Placeholder có ghi "tối thiểu 10 ký tự" nhưng nó biến mất ngay khi
                                      *    người dùng gõ ký tự đầu — đúng lúc luật đó bắt đầu có ý nghĩa. Một
                                      *    nút xám không kèm lý do là ngõ cụt y hệt cái vừa sửa, chỉ lùi lại
                                      *    một bước: đo thật trên máy người dùng, lý do 9 ký tự làm nút khoá
                                      *    im lặng (ảnh chụp 21/09/2026).
                                      */}
                                    <div className={reasonTooShort ? 'VcHint VcDanger' : 'VcHint'}>
                                        {reasonTooShort
                                            ? `Còn thiếu ${MIN_CHANGE_REASON - changeReason.trim().length} ký tự — nút Ghi mở khi lý do đủ ${MIN_CHANGE_REASON} ký tự.`
                                            : 'Lý do này được ghi vào nhật ký thay đổi của công đoạn.'}
                                    </div>
                                </div>
                            )}

                            <div className="VcPreviewActions">
                                <Button
                                    type="primary"
                                    loading={committing}
                                    disabled={!preview.file.groupCount || reasonTooShort}
                                    onClick={handleCommit}
                                >
                                    Ghi {formatInt(preview.file.groupCount)} nhóm
                                </Button>
                            </div>
                            <div className="VcHint">
                                Bấm ghi sẽ gửi lại chính file <code>{preview.file.name}</code> vừa chọn —
                                service không giữ bản xem trước. Ghi thất bại thì không công đoạn nào
                                được lưu.
                            </div>
                        </div>
                    )}
                </TabPane>}

                <TabPane tab="Xuất dữ liệu" key="export">
                    <div className="VcFilterBar">
                        <div className="VcFilterItem">
                            <label>Phạm vi xuất</label>
                            <Select value={exportScope} onChange={setExportScope} style={{width: 240}}>
                                <Option value="all">Toàn bộ công đoạn</Option>
                                {stages.map((stage) => (
                                    <Option key={stage.stageCode} value={stage.stageCode}>{stage.name}</Option>
                                ))}
                            </Select>
                        </div>
                        <div className="VcFilterItem VcFilterItem--action">
                            <Button type="primary" loading={exporting} onClick={handleExport}>
                                Xuất Excel
                            </Button>
                        </div>
                    </div>

                    <Notice level="info">
                        File xuất là <strong>.csv</strong>, đúng các cột ở tab Nhập dữ liệu, dựng từ
                        <strong>toàn bộ nhóm đang quản lý</strong> của công đoạn — gồm cả nhóm chưa tới
                        và đã qua ngày hiệu lực, không chỉ nhóm đang chạy. Đây cũng chính là file mà
                        nút “Tải file mẫu” trả về.
                        {' '}Nạp lại y nguyên thì giá trị không đổi, nhưng nạp file là{' '}
                        <strong>thay toàn bộ nhóm</strong> của mọi công đoạn có trong file, không phải
                        sửa từng dòng — file không còn cột <code>id</code> nên không có cách nào chỉ
                        đích danh một nhóm cũ để cập nhật.
                    </Notice>
                </TabPane>
            </Tabs>
        </div>
    )
}

ImportExportPage.propTypes = {
    // Modal của cha đặt `closable={false}` (không có ✕ ở góc) nên đây là đường đóng DUY NHẤT.
    onClose: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['import', 'export']).isRequired,
    onModeChange: PropTypes.func.isRequired,
}

export default ImportExportPage
